import { yupResolver } from "@hookform/resolvers/yup";
import { Button } from "@omni/ui/components/Button/button.component";
import { cn } from "@omni/utils/styles";

import { AddressRow } from "@/components/Checkout/CheckoutCustomer/addresses/components/addressListModal/components/addressRow/addressRow.component";
import { useAddressBook } from "@/components/Checkout/CheckoutCustomer/addresses/context/addressBook.context";
import { useAddresses } from "@/components/Checkout/CheckoutCustomer/addresses/hooks/useAddresses.hook";
import { useOmniForm } from "@/components/Form/RHF/Utils/useOmniForm";
import { Modal } from "@/components/General/Modal/modal";
import { Table } from "@/components/General/Table/table.component";
import { ModalType } from "@/redux/modules/value/modal/modal.types";
import { useGranite } from "@/redux/modules/value/translations/translations.provider";
import { FormInfoProvider } from "@/utils/forms/formInfo.provider";
import { OmniFormContextProvider } from "@/utils/forms/omniForm.provider";

import {
    AddressBookModalFormValues,
    AddressBookModalValidationSchema
} from "./addressListModal.schema";

type Props = {
    className?: string;
};

const FORM_ID = "addressBookModal";

const AddressListModal = ({ className }: Props) => {
    const { t } = useGranite();

    const { addresses } = useAddresses();

    const {
        loading,
        type,
        select,
        addressId,
        addressListOpen,
        openAddressForm,
        onAddressListClose
    } = useAddressBook();

    const methods = useOmniForm({
        mode: "onBlur",
        reValidateMode: "onBlur",
        values: {
            addressId: addressId
        },
        resolver: yupResolver(AddressBookModalValidationSchema)
    });

    const handleOnSubmit = (values: AddressBookModalFormValues) => {
        if (!values?.addressId) return;

        select(values.addressId, true);
    };

    const handleOnCreate = () => {
        openAddressForm({ type: type });
    };

    return (
        <Modal
            modalType={ModalType.AddressList}
            title={t("general.addressBook.modal.title")}
            formId={FORM_ID}
            size={"sm"}
            confirmButtonText={t("general.addressBook.modal.button.label")}
            isLoading={loading}
            open={addressListOpen}
            className={cn(className)}
            setOpen={onAddressListClose}
        >
            {type && (
                <p>
                    {t(`general.addressBook.modal.text.${type?.toLowerCase()}`)}
                </p>
            )}
            <FormInfoProvider
                formId={FORM_ID}
                withNeverbounce={false}
            >
                {() => (
                    <OmniFormContextProvider {...methods}>
                        <form
                            id={FORM_ID}
                            onSubmit={methods.handleSubmit(handleOnSubmit)}
                        >
                            <Table className={cn("tw:mb-0")}>
                                <tbody>
                                    {addresses.map((address, index) => (
                                        <AddressRow
                                            key={index}
                                            address={address}
                                        />
                                    ))}
                                </tbody>
                            </Table>
                            <hr className={cn("tw:my-0")} />
                            <Button
                                test={"addressBookModal-button-add"}
                                color={"link"}
                                type={"button"}
                                className={cn("tw:my-2")}
                                onClick={handleOnCreate}
                            >
                                <i className={"las la-plus mr-2"} />
                                {t("general.addressBook.modal.add.adress")}
                            </Button>
                        </form>
                    </OmniFormContextProvider>
                )}
            </FormInfoProvider>
        </Modal>
    );
};

export { AddressListModal };
